/**
 * Environment utilities for accessing Vite environment variables
 * Provides clean abstraction that can be mocked in tests
 */

/**
 * Gets a Vite environment variable value
 * @param key - Environment variable key (e.g., 'VITE_DEV_AUTH_MODE')
 * @returns Environment variable value or undefined
 */
export const getEnvVar = (key: string): string | undefined => {
  return import.meta.env[key]
}

/**
 * Checks if development authentication mode is enabled
 * @returns True if VITE_DEV_AUTH_MODE is set to 'true'
 */
export const isDevAuthMode = (): boolean => {
  return getEnvVar('VITE_DEV_AUTH_MODE') === 'true'
}

/**
 * Gets the current environment mode (development, production, etc.)
 * @returns Current Vite mode or 'development' as fallback
 */
export const getMode = (): string => {
  return import.meta.env.MODE || 'development'
}

/**
 * Checks if running in production mode
 * @returns True if MODE is 'production'
 */
export const isProduction = (): boolean => {
  return getMode() === 'production'
}

/**
 * Checks if running in development mode
 * @returns True if MODE is 'development'
 */
export const isDevelopment = (): boolean => {
  return getMode() === 'development'
}

/**
 * Gets the Node.js environment (for server-side or build-time checks)
 * @returns NODE_ENV value or 'development' as fallback
 */
export const getNodeEnv = (): string => {
  return import.meta.env['NODE_ENV'] || 'development'
}

/**
 * Checks if Node environment is production
 * @returns True if NODE_ENV is 'production'
 */
export const isNodeProduction = (): boolean => {
  return getNodeEnv() === 'production'
}